import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
    Clock,
    Users,
    FileSignature,
    Receipt,
    ShieldCheck,
    Zap,
    ArrowRight,
    CheckCircle2,
    Star,
    Building2,
    Utensils,
    Hotel,
    HeartPulse,
    Truck,
    ChevronDown,
    Sparkles,
    LockKeyhole,
    BadgeCheck,
} from "lucide-react";

// -------- Data --------
const STEPS = [
    {
        n: "01",
        icon: Clock,
        title: "Publiez votre mission en 3 minutes",
        body: "Métier, dates, horaires, ville, nombre de personnes : notre configurateur affiche un devis TTC instantané. Aucune inscription obligatoire pour comparer les tarifs.",
    },
    {
        n: "02",
        icon: Users,
        title: "Recevez des profils compatibles",
        body: "Notre moteur géo diffuse la mission aux prestataires vérifiés (SIRET + KBIS + CNI + IA Gemini) dans un rayon défini. Chaque candidature affiche un score de compatibilité.",
    },
    {
        n: "03",
        icon: FileSignature,
        title: "Contrat de prestation généré automatiquement",
        body: "Dès la sélection, un contrat de prestation de services est édité, signé électroniquement par le prestataire et archivé dans votre espace client.",
    },
    {
        n: "04",
        icon: Receipt,
        title: "Facturation automatique",
        body: "Feuille d'heures validée → facture Renforteo (TVA + commission) + facture du prestataire disponibles au téléchargement. Comptabilité prête pour l'expert-comptable.",
    },
    {
        n: "05",
        icon: LockKeyhole,
        title: "Paiement sécurisé Stripe",
        body: "Vos fonds transitent par notre compte plateforme Stripe. Le versement au professionnel intervient uniquement après votre validation du timesheet. Aucune avance à découvert.",
    },
];

const SECTORS = [
    {
        icon: Utensils,
        title: "Restauration & CHR",
        desc: "Serveurs, barmen, chefs de rang, commis, plongeurs, maîtres d'hôtel.",
    },
    {
        icon: Hotel,
        title: "Hôtellerie & événementiel",
        desc: "Hôtes / hôtesses d'accueil, gouvernant(e)s, personnel de banquet, animateurs.",
    },
    {
        icon: Truck,
        title: "Logistique & livraison",
        desc: "Manutentionnaires, chefs d'équipe, livreurs, livreurs-monteurs, personnel logistique événementielle.",
    },
    {
        icon: Sparkles,
        title: "Entretien & propreté",
        desc: "Agents de propreté, femmes / hommes de ménage pour hôtels, bureaux et résidences.",
    },
];

const TESTIMONIALS = [
    {
        name: "Exemple de situation",
        role: "Groupe hôtelier · Lyon (cas type)",
        stars: 5,
        text: "Recherche de 12 serveurs pour un dîner de gala au Palais de la Bourse : devis publié en fin de matinée, brigade complète confirmée sous 24 h. Contrats et factures générés automatiquement dans la plateforme.",
    },
    {
        name: "Exemple de situation",
        role: "Salle de réception · Valence (cas type)",
        stars: 5,
        text: "Recours à plusieurs agences d'intérim en parallèle avant Renforteo. Sur la plateforme : un seul devis TTC, un seul interlocuteur, une facture par mission. Comptabilité simplifiée et coûts maîtrisés.",
    },
    {
        name: "Exemple de situation",
        role: "Centre de congrès · Grenoble (cas type)",
        stars: 5,
        text: "Le versement uniquement après validation du timesheet est rassurant pour les deux parties. Les profils vérifiés par IA + contrôle humain apportent un niveau de fiabilité supérieur au recrutement direct.",
    },
];

const PRICING = [
    {
        label: "Honoraires HT du professionnel",
        detail: "Tarif horaire HT défini par le pro (à partir de 16 €/h) — majorations nuit / dimanche / férié",
        pct: "≈ 80 %",
    },
    {
        label: "Frais Renforteo (mise en relation + paiement sécurisé + support)",
        detail: "Inclus dans le devis TTC · aucun frais caché",
        pct: "15 %",
    },
    {
        label: "TVA (selon statut du pro & Renforteo)",
        detail: "Pro en franchise base (art. 293 B CGI) : 0 % sur la prestation. Sinon 20 %. La commission Renforteo est soumise à TVA 20 %.",
        pct: "0-20 %",
    },
];

const FAQ = [
    {
        q: "Suis-je considéré comme employeur ?",
        a: "Non. Vous êtes donneur d'ordre. Le prestataire est un auto-entrepreneur immatriculé (SIRET vérifié) qui intervient pour son propre compte. Aucune déclaration sociale n'est à votre charge : ni contrat de travail, ni charge patronale, ni bulletin de paie.",
    },
    {
        q: "Comment les profils sont-ils vérifiés ?",
        a: "SIRET (RCS ou INSEE), KBIS ou fiche INSEE, carte d'identité recto + verso (vérification IA Gemini), justificatif de domicile, RIB. Selon le métier, une attestation HACCP peut être demandée pour la cuisine. Contrôle manuel systématique sous 24 h ouvrées.",
    },
    {
        q: "Quels sont les délais de mise à disposition ?",
        a: "En moyenne 6 heures en semaine pour recevoir les premières candidatures dans les grandes métropoles. Les missions urgentes déclenchent une alerte prioritaire aux « Super Talents » (10+ missions terminées, note ≥ 4,7 / 5, zéro no-show).",
    },
    {
        q: "Puis-je annuler une mission ?",
        a: "Oui. Politique claire : gratuit jusqu'à 72 h avant, 50 % du montant entre 72 h et 24 h, 100 % sous 24 h (indemnisation du prestataire déjà mobilisé). Détail complet dans notre Politique d'annulation.",
    },
    {
        q: "Que se passe-t-il en cas de no-show du prestataire ?",
        a: "Signalement en 1 clic dans votre espace client → pénalité automatique (1er = avertissement, 2ᵉ = suspension). Notre équipe active immédiatement le dispositif de remplacement auprès des prestataires disponibles dans le rayon.",
    },
    {
        q: "Puis-je créer une équipe habituelle ?",
        a: "Oui. La fonction « Mes équipes » permet de mémoriser vos prestataires favoris (Cocktail Lyon, Sécurité Grenoble, etc.) et de les inviter en priorité sur vos prochaines missions. Widget disponibilité en direct dans le configurateur.",
    },
    {
        q: "Comment fonctionne la facturation multi-missions ?",
        a: "Chaque mission génère automatiquement 2 factures : la facture Renforteo (frais de plateforme + TVA sur ces frais) et la facture du professionnel (honoraires HT — TVA appliquée uniquement si le pro est assujetti à TVA). Elles sont téléchargeables individuellement ou en export mensuel comptable.",
    },
    {
        q: "Un compte est-il obligatoire pour obtenir un devis ?",
        a: "Non. Notre configurateur affiche le devis TTC en direct sans inscription. Le compte n'est créé qu'au moment du paiement, avec une adresse e-mail et un nom d'entreprise.",
    },
];

const TRUST_METRICS = [
    { value: "24 h", label: "délai moyen de matching" },
    { value: "15 %", label: "frais entreprise tout compris, aucun abonnement" },
    { value: "100 %", label: "professionnels vérifiés IA + humain" },
    { value: "0 €", label: "abonnement, engagement ou frais cachés" },
];

// -------- SEO / JSON-LD --------
function useEntreprisesSEO() {
    useEffect(() => {
        document.title = "Renforteo Entreprises — Publiez une mission en 3 min, personnel vérifié en 24 h";
        const setMeta = (name, content, attr = "name") => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        const desc =
            "Renforteo Entreprises : plateforme B2B pour trouver du personnel qualifié indépendant en 24 h (serveurs, cuisiniers, hôtes, agents d'entretien, manutentionnaires). Devis TTC instantané, contrat de prestation auto, paiement sécurisé Stripe, aucune déclaration sociale à votre charge.";
        setMeta("description", desc);
        setMeta("og:title", "Renforteo Entreprises — Personnel qualifié en 24 h");
        setMeta("og:description", desc);
        setMeta("og:type", "website");
        setMeta("og:url", "https://renforteo.fr/entreprises");
        setMeta("twitter:card", "summary_large_image");

        // JSON-LD Service + FAQPage
        const jsonld = {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "Service",
                    name: "Renforteo Entreprises — Recrutement B2B de personnel qualifié",
                    provider: {
                        "@type": "Organization",
                        name: "Renforteo",
                        url: "https://renforteo.fr",
                    },
                    serviceType: "Mise en relation B2B de personnel événementiel, restauration, sécurité et santé",
                    areaServed: ["Lyon", "Villeurbanne", "Grenoble", "Valence", "Auvergne-Rhône-Alpes"],
                    audience: { "@type": "BusinessAudience" },
                    hasOfferCatalog: {
                        "@type": "OfferCatalog",
                        name: "Missions Renforteo",
                        itemListElement: SECTORS.map((s) => ({
                            "@type": "Offer",
                            itemOffered: { "@type": "Service", name: s.title, description: s.desc },
                        })),
                    },
                },
                {
                    "@type": "FAQPage",
                    mainEntity: FAQ.map((f) => ({
                        "@type": "Question",
                        name: f.q,
                        acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                },
            ],
        };
        let ld = document.getElementById("entreprises-jsonld");
        if (!ld) {
            ld = document.createElement("script");
            ld.type = "application/ld+json";
            ld.id = "entreprises-jsonld";
            document.head.appendChild(ld);
        }
        ld.textContent = JSON.stringify(jsonld);

        // canonical
        let canon = document.querySelector("link[rel=canonical]");
        if (!canon) {
            canon = document.createElement("link");
            canon.setAttribute("rel", "canonical");
            document.head.appendChild(canon);
        }
        canon.setAttribute("href", "https://renforteo.fr/entreprises");

        return () => {
            // leave meta in place — Landing/Local pages overwrite on nav
        };
    }, []);
}

// -------- Page --------
export default function EntreprisesPage() {
    useEntreprisesSEO();

    return (
        <div data-testid="entreprises-page">
            <Header />

            {/* -------- HERO -------- */}
            <section
                className="relative overflow-hidden bg-[#0B192C] text-white"
                data-testid="entreprises-hero"
            >
                <div className="absolute inset-0 opacity-20 pointer-events-none"
                     style={{
                         backgroundImage:
                             "radial-gradient(circle at 20% 20%, rgba(245,166,35,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(245,166,35,0.25) 0%, transparent 40%)",
                     }}
                />
                <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-24 md:pt-28 md:pb-32 relative">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#F5A623]/40 bg-[#F5A623]/10 px-4 py-1.5 mb-8">
                            <BadgeCheck size={14} className="text-[#F5A623]" />
                            <span className="text-xs uppercase tracking-widest font-semibold text-[#F5A623]">
                                Renforteo Entreprises · B2B
                            </span>
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight mb-6">
                            Recrutez du personnel qualifié
                            <br />
                            <span className="text-[#F5A623]">en 24 heures.</span>
                        </h1>
                        <p className="text-base md:text-lg text-white/80 leading-relaxed mb-10 max-w-2xl">
                            Vous avez une absence, un événement ou un pic d'activité ? Publiez votre
                            besoin en 60 secondes et recevez des candidatures de professionnels vérifiés
                            disponibles près de chez vous.{" "}
                            <span className="text-white font-semibold">
                                Contrat, paiement et facture sont gérés en ligne.
                            </span>
                        </p>
                        <div className="flex flex-wrap gap-4 items-center">
                            <Link
                                to="/devis"
                                className="btn-gold text-sm md:text-base"
                                data-testid="entreprises-hero-cta-quote"
                            >
                                Estimer ma mission
                                <ArrowRight size={16} className="inline ml-2 -mt-0.5" />
                            </Link>
                            <Link
                                to="/inscription"
                                className="text-sm md:text-base text-white/90 hover:text-[#F5A623] underline underline-offset-4 font-semibold"
                                data-testid="entreprises-hero-cta-register"
                            >
                                Créer mon espace entreprise
                            </Link>
                        </div>
                    </div>

                    {/* Trust strip */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-10 border-t border-white/10">
                        {TRUST_METRICS.map((m, i) => (
                            <div key={i} data-testid={`trust-metric-${i}`}>
                                <div className="font-display text-3xl md:text-4xl font-black text-[#F5A623]">
                                    {m.value}
                                </div>
                                <div className="text-xs text-white/70 mt-1 leading-tight">
                                    {m.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* -------- OFFRE 1ère MISSION -------- */}
            <section
                className="bg-gradient-to-r from-emerald-50 via-white to-emerald-50 border-y border-emerald-200"
                data-testid="entreprises-first-mission-offer"
            >
                <div className="max-w-6xl mx-auto px-6 md:px-10 py-8 md:py-10 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                            <Sparkles size={22} />
                        </div>
                        <div>
                            <div className="inline-block bg-emerald-600 text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full mb-2">
                                Offre de lancement
                            </div>
                            <div className="font-display text-lg md:text-xl text-[#0B192C] leading-tight">
                                Première mission&nbsp;:{" "}
                                <span className="text-emerald-700 font-black">0 % de frais Renforteo côté entreprise</span>.
                            </div>
                            <p className="text-xs md:text-sm text-slate-500 leading-relaxed mt-1 max-w-lg">
                                Testez la plateforme sans risque. Vous ne payez que les honoraires
                                du professionnel — les frais Renforteo sont offerts sur votre 1<sup>re</sup>
                                mission. Offre sans code, appliquée automatiquement au premier compte
                                entreprise.
                            </p>
                        </div>
                    </div>
                    <Link
                        to="/devis"
                        className="btn-gold shrink-0 whitespace-nowrap"
                        data-testid="entreprises-first-mission-cta"
                    >
                        Estimer ma première mission
                        <ArrowRight size={16} className="inline ml-2 -mt-0.5" />
                    </Link>
                </div>
            </section>

            {/* -------- HOW IT WORKS (5 steps) -------- */}
            <section
                className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24"
                data-testid="entreprises-steps"
            >
                <div className="text-center mb-14">
                    <div className="overline mb-3">Le parcours entreprise</div>
                    <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                        Cinq étapes, une seule plateforme.
                    </h2>
                    <p className="text-slate-500 max-w-2xl mx-auto mt-4">
                        Du devis à la facture, tout est automatisé. Vous gardez la main sur la
                        sélection ; on s'occupe du reste.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={i}
                                className="sd-card p-5 flex flex-col relative overflow-hidden group hover:border-[#F5A623] transition-colors"
                                data-testid={`entreprises-step-${i}`}
                            >
                                <div className="absolute top-4 right-4 font-display text-4xl font-black text-slate-100 group-hover:text-[#F5A623]/20 transition-colors leading-none">
                                    {s.n}
                                </div>
                                <div className="w-11 h-11 rounded-xl bg-[#0B192C] text-[#F5A623] flex items-center justify-center mb-4">
                                    <Icon size={20} />
                                </div>
                                <h3 className="font-display text-lg font-bold text-[#0B192C] mb-2 leading-tight">
                                    {s.title}
                                </h3>
                                <p className="text-xs text-slate-500 leading-relaxed">
                                    {s.body}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* -------- SECTORS -------- */}
            <section
                className="bg-slate-50 border-y border-slate-200"
                data-testid="entreprises-sectors"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="grid md:grid-cols-[1fr_2fr] gap-10 items-start">
                        <div>
                            <div className="overline mb-3">Secteurs couverts</div>
                            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                                Six secteurs.
                                <br />
                                Un standard qualité.
                            </h2>
                            <p className="text-slate-500 mt-4 leading-relaxed max-w-md">
                                Quatre familles métiers actives sur la plateforme. Chaque prestataire est
                                validé métier par métier : chef avec ses attestations HACCP, livreur-monteur
                                avec son expérience assemblage confirmée, personnel événementiel avec ses
                                références clients.
                            </p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {SECTORS.map((s, i) => {
                                const Icon = s.icon;
                                return (
                                    <div
                                        key={i}
                                        className="bg-white rounded-xl p-5 border border-slate-200 hover:border-[#F5A623] transition-colors"
                                        data-testid={`entreprises-sector-${i}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center shrink-0">
                                                <Icon size={18} />
                                            </div>
                                            <div>
                                                <div className="font-display text-base font-bold text-[#0B192C] mb-1">
                                                    {s.title}
                                                </div>
                                                <p className="text-xs text-slate-500 leading-relaxed">
                                                    {s.desc}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* -------- TESTIMONIALS -------- */}
            <section
                className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24"
                data-testid="entreprises-testimonials"
            >
                <div className="text-center mb-14">
                    <div className="overline mb-3">Ils utilisent Renforteo</div>
                    <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                        Ce qu'en disent nos clients.
                    </h2>
                </div>
                <div className="grid md:grid-cols-3 gap-5">
                    {TESTIMONIALS.map((t, i) => (
                        <blockquote
                            key={i}
                            className="sd-card p-6 flex flex-col"
                            data-testid={`entreprises-testimonial-${i}`}
                        >
                            <div className="flex gap-0.5 mb-3">
                                {Array.from({ length: t.stars }).map((_, k) => (
                                    <Star key={k} size={14} className="fill-[#F5A623] text-[#F5A623]" />
                                ))}
                            </div>
                            <p className="text-sm text-slate-700 leading-relaxed mb-5 flex-1">
                                « {t.text} »
                            </p>
                            <div className="pt-4 border-t border-slate-100">
                                <div className="font-semibold text-sm text-[#0B192C]">
                                    {t.name}
                                </div>
                                <div className="text-xs text-slate-500">
                                    {t.role}
                                </div>
                            </div>
                        </blockquote>
                    ))}
                </div>
            </section>

            {/* -------- PRICING -------- */}
            <section
                className="bg-[#0B192C] text-white"
                data-testid="entreprises-pricing"
            >
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="text-center mb-14">
                        <div className="overline text-white/60 mb-3">Tarification transparente</div>
                        <h2 className="font-display text-3xl md:text-4xl tracking-tight text-white">
                            Un seul prix TTC.
                            <br />
                            <span className="text-[#F5A623]">Aucun frais caché.</span>
                        </h2>
                        <p className="text-white/70 max-w-2xl mx-auto mt-4">
                            Le montant affiché dans le configurateur est le montant que vous payez.
                            Pas d'abonnement, pas de frais de dossier, pas de commission variable
                            selon le prestataire.
                        </p>
                    </div>

                    <div className="grid gap-3">
                        {PRICING.map((p, i) => (
                            <div
                                key={i}
                                className="grid grid-cols-[1fr_120px] md:grid-cols-[2fr_1fr_120px] gap-4 items-center border border-white/10 rounded-xl p-5 bg-white/5"
                                data-testid={`entreprises-pricing-row-${i}`}
                            >
                                <div>
                                    <div className="font-semibold text-white text-sm md:text-base">
                                        {p.label}
                                    </div>
                                </div>
                                <div className="hidden md:block text-xs text-white/60 leading-relaxed">
                                    {p.detail}
                                </div>
                                <div className="text-right">
                                    <span className="inline-block px-3 py-1.5 rounded-full bg-[#F5A623] text-[#0B192C] font-black text-sm">
                                        {p.pct}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 flex items-start gap-3 p-5 rounded-xl border border-[#F5A623]/40 bg-[#F5A623]/10">
                        <CheckCircle2 size={20} className="text-[#F5A623] shrink-0 mt-0.5" />
                        <div className="text-sm text-white/85 leading-relaxed">
                            <b className="text-white">Paiement sécurisé Stripe :</b> vos fonds
                            transitent par notre compte plateforme et ne sont libérés au
                            professionnel qu'après votre validation du timesheet. Aucun risque de
                            payer une prestation non livrée. En cas de litige, notre équipe intervient
                            en médiation.
                        </div>
                    </div>

                    <div className="text-center mt-10">
                        <Link
                            to="/devis"
                            className="btn-gold text-sm md:text-base"
                            data-testid="entreprises-pricing-cta"
                        >
                            Voir mon devis exact
                            <ArrowRight size={16} className="inline ml-2 -mt-0.5" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* -------- FAQ -------- */}
            <section
                className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-24"
                data-testid="entreprises-faq"
            >
                <div className="text-center mb-12">
                    <div className="overline mb-3">FAQ Entreprises</div>
                    <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                        Vos questions, nos réponses.
                    </h2>
                </div>
                <div className="space-y-3">
                    {FAQ.map((f, i) => (
                        <details
                            key={i}
                            className="sd-card p-5 md:p-6 group"
                            data-testid={`entreprises-faq-item-${i}`}
                        >
                            <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                                <span className="font-display text-base md:text-lg text-[#0B192C]">
                                    {f.q}
                                </span>
                                <ChevronDown
                                    size={20}
                                    className="shrink-0 text-slate-400 mt-1 transition-transform group-open:rotate-180"
                                />
                            </summary>
                            <p className="text-sm text-slate-500 leading-relaxed mt-3">
                                {f.a}
                            </p>
                        </details>
                    ))}
                </div>
            </section>

            {/* -------- FINAL CTA -------- */}
            <section
                className="bg-gradient-to-br from-[#F5A623] to-[#f7b74a] text-[#0B192C]"
                data-testid="entreprises-final-cta"
            >
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20 text-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-[#0B192C] text-white px-4 py-1.5 mb-6">
                        <Zap size={14} className="text-[#F5A623]" />
                        <span className="text-xs uppercase tracking-widest font-semibold">
                            3 minutes suffisent
                        </span>
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight mb-6 max-w-3xl mx-auto">
                        Prêt à publier votre première mission&nbsp;?
                    </h2>
                    <p className="text-sm md:text-base text-[#0B192C]/80 max-w-2xl mx-auto mb-10">
                        Aucune carte bancaire n'est demandée pour obtenir votre devis.
                        Vous ne payez qu'au moment de valider la mission.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <Link
                            to="/devis"
                            className="inline-flex items-center gap-2 bg-[#0B192C] text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-[#132845] transition-colors"
                            data-testid="entreprises-final-cta-quote"
                        >
                            <Building2 size={16} />
                            Estimer ma mission
                        </Link>
                        <Link
                            to="/assistance"
                            className="inline-flex items-center gap-2 border-2 border-[#0B192C] text-[#0B192C] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-[#0B192C] hover:text-white transition-colors"
                            data-testid="entreprises-final-cta-contact"
                        >
                            Parler à un expert Renforteo
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
